import { RefObject, useCallback, useEffect, useRef } from 'react'
import { getScreenState } from '../../app-state/state.screen'

const SWIPE_THRESHOLD = 0.1

const emptyFn = () => {}

interface UseSwipeActionOptions {
    condition?: boolean
    onSwipeLeft?: () => void
    onSwipeRight?: () => void
}

export const useSwipeAction = (
    ref: RefObject<HTMLElement>,
    options: UseSwipeActionOptions = {},
) => {
    const {
        condition = true,
        onSwipeLeft = emptyFn,
        onSwipeRight = emptyFn,
    } = options

    const touchScratchPadRef = useRef<{
        // starting x of the drag
        startX: number
        // starting y of the drag
        startY: number
        // if user has swiped left/right enough to change rooms
        swipeThreshold: boolean
        // direction of swipe
        swipeDirection: 'left' | 'right' | undefined
    }>(null)

    const onTouchStart = useCallback((event: TouchEvent) => {
        const { clientY, clientX } = event.touches[0]

        touchScratchPadRef.current = {
            startX: clientX,
            startY: clientY,
            swipeThreshold: false,
            swipeDirection: undefined,
        }
    }, [])

    const onTouchMove = useCallback((event: TouchEvent) => {
        const { touches } = event
        const { clientX } = touches[0]

        const { startX: touchStartX } = touchScratchPadRef.current
        const { windowWidth } = getScreenState()

        const dragPercentX = Math.abs(clientX - touchStartX) / windowWidth
        const dragDirectionX = clientX - touchStartX > 0 ? 'right' : 'left'

        if (dragPercentX > SWIPE_THRESHOLD) {
            touchScratchPadRef.current.swipeThreshold = true
            touchScratchPadRef.current.swipeDirection = dragDirectionX
        }
    }, [])

    const onTouchEnd = useCallback(
        (event: TouchEvent) => {
            const { changedTouches } = event
            if (changedTouches && changedTouches.length < 1) return
            const { swipeThreshold, swipeDirection } =
                touchScratchPadRef.current
            touchScratchPadRef.current = null

            if (swipeThreshold) {
                if (swipeDirection === 'right') {
                    onSwipeRight()
                } else {
                    onSwipeLeft()
                }
            }
        },
        [onSwipeLeft, onSwipeRight],
    )

    useEffect(() => {
        const node = ref.current
        if (node) {
            if (condition) {
                node.addEventListener('touchstart', onTouchStart)
                node.addEventListener('touchmove', onTouchMove)
                node.addEventListener('touchend', onTouchEnd)
            } else {
                node.removeEventListener('touchstart', onTouchStart)
                node.removeEventListener('touchmove', onTouchMove)
                node.removeEventListener('touchend', onTouchEnd)
            }
        }
        return () => {
            if (node) {
                node.removeEventListener('touchstart', onTouchStart)
                node.removeEventListener('touchmove', onTouchMove)
                node.removeEventListener('touchend', onTouchEnd)
            }
        }
    }, [ref, condition, onTouchStart, onTouchMove, onTouchEnd])
}
